import { logger } from './logger.js';

/**
 * Client API pour les endpoints du widget
 */
export class WidgetAPI {
  constructor(baseUrl) {
    // Vérification de sécurité pour s'assurer qu'on a une baseUrl valide
    if (
      !baseUrl ||
      (!baseUrl.startsWith('http://') && !baseUrl.startsWith('https://'))
    ) {
      // Toujours utiliser ringado.com comme fallback
      this.baseUrl = 'https://ringado.com';
      logger.warn('Invalid baseUrl provided, using fallback', {
        provided: baseUrl,
        fallback: this.baseUrl,
      });
    } else {
      this.baseUrl = baseUrl;
    }

    logger.info('WidgetAPI initialized', { baseUrl: this.baseUrl });
  }

  /**
   * Effectue un appel API avec gestion d'erreurs
   */
  async apiCall(endpoint, options = {}) {
    const url = `${this.baseUrl}${endpoint}`;

    try {
      logger.info('🌐 Widget API Call', {
        endpoint,
        method: options.method || 'GET',
        url: url,
      });

      const response = await fetch(url, {
        headers: {
          'Content-Type': 'application/json',
          ...options.headers,
        },
        ...options,
      });

      if (!response.ok) {
        let errorData = {};
        let responseText = '';

        try {
          responseText = await response.text();
          errorData = JSON.parse(responseText);
        } catch (parseError) {
          // Si la réponse n'est pas du JSON valide, on utilise le texte brut
          errorData = {
            statusMessage: responseText || `HTTP ${response.status}`,
            rawResponse: responseText,
          };
        }

        const errorMessage =
          errorData.statusMessage ||
          errorData.message ||
          `HTTP ${response.status}`;

        // Log détaillé pour le debug dans Framer
        logger.error('❌ API Response Error', {
          endpoint,
          status: response.status,
          statusText: response.statusText,
          errorData,
          responseText: responseText.substring(0, 500), // Limiter pour éviter les logs trop longs
          responseHeaders: Object.fromEntries(response.headers.entries()),
          isFramerEnv: window.location.href?.includes('framer') || false,
        });

        // Pour les erreurs 500, ajouter plus de contexte
        if (response.status === 500) {
          const enhancedError = new Error(
            `Server Error (500): ${errorMessage}. This might be due to invalid data in the request payload.`
          );
          enhancedError.originalError = errorData;
          enhancedError.status = response.status;
          throw enhancedError;
        }

        const error = new Error(errorMessage);
        error.status = response.status;
        error.responseData = errorData;
        throw error;
      }

      const data = await response.json();
      logger.info('✅ Widget API Success', { endpoint, success: data.success });

      return data;
    } catch (error) {
      // Log plus détaillé pour identifier les problèmes Framer
      logger.error('❌ Widget API Error', {
        endpoint,
        error: error.message,
        errorType: error.constructor.name,
        stack: error.stack?.split('\n').slice(0, 3).join('\n'),
        url: url,
        isFramerEnv: window.location.href?.includes('framer') || false,
      });

      throw error;
    }
  }

  /**
   * Créer une nouvelle conversation
   */
  async createConversation(visitorData, operatorId, initialMessage = null) {
    // Fonction utilitaire pour obtenir une valeur sûre
    const getSafeValue = (getValue, fallback = '') => {
      try {
        const value = getValue();
        return value || fallback;
      } catch (error) {
        logger.warn('Error getting value, using fallback:', error.message);
        return fallback;
      }
    };

    // Fonction pour nettoyer et valider les chaînes
    const sanitizeString = (str, maxLength = 1000) => {
      if (!str || typeof str !== 'string') return '';

      // Supprimer les caractères de contrôle et caractères non-printables
      const cleaned = str.replace(/[\x00-\x1F\x7F]/g, '').trim();

      // Limiter la longueur
      return cleaned.length > maxLength
        ? cleaned.substring(0, maxLength)
        : cleaned;
    };

    const rawPageUrl = getSafeValue(
      () => window.location.href,
      'https://unknown-domain.com'
    );
    const rawPageTitle = getSafeValue(() => document.title, 'Unknown Page');
    const rawUserAgent = getSafeValue(
      () => navigator.userAgent,
      'Unknown Browser'
    );

    const payload = {
      visitorName: sanitizeString(visitorData.name) || 'Anonymous Visitor',
      visitorEmail: sanitizeString(visitorData.email) || '',
      operatorId: operatorId,
      pageUrl: sanitizeString(rawPageUrl, 500),
      pageTitle: sanitizeString(rawPageTitle, 200),
      userAgent: sanitizeString(rawUserAgent, 500),
      userId: visitorData.userId || null,
    };

    // Validation supplémentaire pour Framer
    if (
      !payload.operatorId ||
      payload.operatorId === 'null' ||
      payload.operatorId === 'undefined'
    ) {
      throw new Error('Invalid operator ID provided');
    }

    // Nettoyer les valeurs potentiellement problématiques pour Framer
    if (
      payload.pageUrl.includes('framer.com') ||
      payload.pageUrl.includes('framer.website') ||
      payload.pageUrl.includes('framercanvas.com')
    ) {
      logger.info('Framer environment detected, adapting payload');

      // S'assurer que l'URL est valide
      if (!payload.pageUrl.startsWith('http')) {
        payload.pageUrl = 'https://' + payload.pageUrl;
      }

      // Pour Framer, simplifier le titre qui peut contenir des caractères étranges
      if (
        payload.pageTitle.length === 0 ||
        payload.pageTitle === 'Unknown Page'
      ) {
        payload.pageTitle = 'Framer Page';
      }

      // Simplifier l'User Agent pour Framer s'il est trop complexe
      if (payload.userAgent.length > 200) {
        payload.userAgent = 'Mozilla/5.0 (Framer Environment)';
      }
    }

    if (initialMessage && initialMessage.trim()) {
      payload.initialMessage = sanitizeString(initialMessage.trim(), 2000);
    }

    // Log complet du payload pour debug
    logger.info('Creating conversation with payload:', {
      operatorId: payload.operatorId,
      visitorName: payload.visitorName,
      visitorEmail: payload.visitorEmail,
      pageUrl: payload.pageUrl,
      pageTitle: payload.pageTitle,
      userAgent: payload.userAgent.substring(0, 100) + '...',
      hasUserId: !!payload.userId,
      hasInitialMessage: !!payload.initialMessage,
      payloadSize: JSON.stringify(payload).length,
    });

    // Validation finale de la taille du payload
    const payloadString = JSON.stringify(payload);
    if (payloadString.length > 10000) {
      // 10KB max
      logger.warn('Payload too large, truncating user agent');
      payload.userAgent = 'Mozilla/5.0 (Truncated for size)';
    }

    const response = await this.apiCall('/api/widget/conversation', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response.data;
  }

  /**
   * Envoyer un message
   */
  async sendMessage(conversationId, content, senderData) {
    const payload = {
      conversationId: conversationId,
      content: content.trim(),
      senderType: senderData.type || 'visitor',
      senderName: senderData.name,
      userId: senderData.userId || null,
    };

    const response = await this.apiCall('/api/widget/message', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response.data;
  }

  /**
   * Récupérer les messages d'une conversation
   */
  async getMessages(conversationId, lastMessageId = null, limit = 50) {
    const params = new URLSearchParams({
      conversationId: conversationId,
      limit: limit.toString(),
    });

    if (lastMessageId) {
      params.append('lastMessageId', lastMessageId);
    }

    const response = await this.apiCall(
      `/api/widget/messages?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Vérifier le statut d'une conversation
   */
  async getConversationStatus(conversationId) {
    const params = new URLSearchParams({
      conversationId: conversationId,
    });

    const response = await this.apiCall(
      `/api/widget/status?${params.toString()}`
    );

    return response.data;
  }

  /**
   * Connecter un opérateur à une conversation
   */
  async connectOperator(conversationId, operatorId) {
    const payload = {
      conversationId: conversationId,
      operatorId: operatorId,
    };

    const response = await this.apiCall('/api/widget/connect-operator', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response.data;
  }

  /**
   * Fermer une conversation
   */
  async closeConversation(conversationId, reason = 'Closed by visitor') {
    const payload = {
      conversationId: conversationId,
      reason: reason,
    };

    const response = await this.apiCall('/api/widget/close-conversation', {
      method: 'POST',
      body: JSON.stringify(payload),
    });

    return response.data;
  }
}
